import { Tooltip as MantineTooltip, TooltipProps, useMantineTheme, MantineSize } from '@mantine/core';
import { forwardRef, ReactNode } from 'react';
import { useMediaQuery } from '@mantine/hooks';
import { usePreferencesStore } from '../../../stores';
import { LINE_HEIGHT_MAP, LETTER_SPACING_MAP } from '../../../hooks/useAccessibilityStyles';

export type GradientVariant =
  | 'primary'
  | 'secondary'
  | 'tertiary'
  | 'success'
  | 'info'
  | 'warning'
  | 'danger';

export interface ThemedTooltipProps extends TooltipProps {
  gradient?: boolean;
  gradientVariant?: GradientVariant;
  responsiveBreakpoint?: number | MantineSize;
}

const Tooltip = forwardRef<HTMLDivElement, ThemedTooltipProps>(
  ({ gradient = false, gradientVariant = 'primary', styles, children, responsiveBreakpoint = 'sm', ...rest }, ref) => {
    const theme = useMantineTheme();
    const gradientConfig = theme.other.gradients[gradientVariant];
    const lineSpacing = usePreferencesStore((state) => state.lineSpacing);
    const letterSpacingPref = usePreferencesStore((state) => state.letterSpacing);

    const accessibilityStyles = {
      lineHeight: LINE_HEIGHT_MAP[lineSpacing],
      letterSpacing: LETTER_SPACING_MAP[letterSpacingPref],
    };
    const breakpointValue =
      typeof responsiveBreakpoint === 'number'
        ? responsiveBreakpoint
        : theme.breakpoints?.[responsiveBreakpoint as MantineSize];
    const isSmallScreen = breakpointValue ? useMediaQuery(`(max-width: ${breakpointValue}px)`) : false;
    const commonProps = {
      position: isSmallScreen ? 'bottom' : rest.position,
      withinPortal: true,
      transitionProps: { duration: rest.transitionProps?.duration ?? (isSmallScreen ? 100 : 150) },
    };

    if (!gradient) {
      return (
        <MantineTooltip
          ref={ref}
          styles={{
            ...styles,
            tooltip: {
              ...accessibilityStyles,
              ...(styles as any)?.tooltip,
            },
          }}
          {...commonProps}
          {...rest}
        >
          {children}
        </MantineTooltip>
      );
    }

    return (
      <MantineTooltip
        ref={ref}
        styles={{
          ...styles,
          tooltip: {
            backgroundImage: `linear-gradient(135deg, ${gradientConfig.from}, ${gradientConfig.to})`,
            color: 'white',
            ...accessibilityStyles,
            ...(styles as any)?.tooltip,
          },
        }}
        {...commonProps}
        {...rest}
      >
        {children}
      </MantineTooltip>
    );
  }
);

Tooltip.displayName = 'Tooltip';

export default Tooltip;
